import { toast } from "@birdwatch/ui/toast";
import { cn } from "cn";

import { BwWarningTriangleLine } from "@/components/ui/icons";
import { placeholdersLeftNote } from "../_libs/placeholders-left";

/**
 * Above the box of a composer that speaks to a customer, while its text still
 * has a `[placeholder: …]`: says how many blanks are left and names them, and
 * updates as the person types. The live region is always rendered, empty when
 * nothing is left: a screen reader announces text that changes inside a region
 * it already knows, and often misses one that arrives with its text.
 */
export function PlaceholderNote({ content }: { content: string }) {
  const note = placeholdersLeftNote(content);
  return (
    <div role="status">
      {note ? (
        <p
          className={cn(
            /* sorted */
            "flex",
            "items-start gap-1.5",
            "w-full",
            "mb-2 px-3 py-1.5",
            "text-sm text-text-dark-high",
            "bg-bg-warning-min rounded-2xl",
          )}
        >
          <BwWarningTriangleLine
            className={cn(
              /* sorted */
              "shrink-0",
              "size-4",
              "mt-0.5",
              "text-text-warning",
            )}
            aria-hidden
          />
          <span className="min-w-0">{note}</span>
        </p>
      ) : null}
    </div>
  );
}

/**
 * A composer's `onBeforeSend` check for a message to a customer. While the
 * text still has a blank, a "Message not sent" toast names what is left and the
 * answer is true: the send stops. False when nothing is left to fill in.
 */
export function refuseSendWithPlaceholders(content: string): boolean {
  const note = placeholdersLeftNote(content);
  if (!note) return false;
  toast.error({ title: "Message not sent", description: note });
  return true;
}
